import { useEffect, useRef, useState } from 'react';
import { AlertTriangle, FileSignature } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';

/**
 * ESignatureModal — reusable 21 CFR Part 11 compliant e-signature dialog.
 *
 * The signature body is shaped for the backend {@link ESignatureSchema} in
 * packages/shared/src/schemas/auth.ts, which accepts { password, reason }.
 * We also capture an "intent" (what the signer intends the action to mean)
 * for local UX / audit clarity; callers decide whether to forward it or fold
 * it into the reason string sent on the wire.
 *
 * Conforms to Part 11.200(b) by requiring password re-authentication each
 * time a signature is executed, and Part 11.50(a) by capturing the meaning
 * (reason) of the signature.
 */
export interface ESignaturePayload {
  password: string;
  reason: string;
  intent: string;
}

export interface ESignatureReasonPreset {
  value: string;
  label: string;
}

export interface ESignatureModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description: string;
  reasonPresets?: ESignatureReasonPreset[];
  onConfirm: (signature: ESignaturePayload) => Promise<void>;
  isSubmitting?: boolean;
  submitLabel?: string;
}

const MIN_INTENT_LENGTH = 10;

export function ESignatureModal({
  open,
  onOpenChange,
  title,
  description,
  reasonPresets,
  onConfirm,
  isSubmitting = false,
  submitLabel = 'Sign and confirm',
}: ESignatureModalProps) {
  const [password, setPassword] = useState('');
  const [reasonSelect, setReasonSelect] = useState<string>('');
  const [reasonCustom, setReasonCustom] = useState('');
  const [intent, setIntent] = useState('');
  const [agreed, setAgreed] = useState(false);
  const passwordRef = useRef<HTMLInputElement | null>(null);

  const hasPresets = !!reasonPresets && reasonPresets.length > 0;
  const useCustomReason = !hasPresets || reasonSelect === '__custom__';
  const reasonValue = useCustomReason ? reasonCustom.trim() : reasonSelect;

  // Reset fields whenever the modal transitions from closed to open so a
  // fresh signature is captured every time (Part 11.70 — no reuse).
  useEffect(() => {
    if (open) {
      setPassword('');
      setReasonSelect(hasPresets ? reasonPresets![0].value : '');
      setReasonCustom('');
      setIntent('');
      setAgreed(false);
      // Defer focus until the dialog content is mounted.
      const t = setTimeout(() => passwordRef.current?.focus(), 50);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [open, hasPresets, reasonPresets]);

  const intentTooShort = intent.trim().length < MIN_INTENT_LENGTH;
  const disabled =
    isSubmitting ||
    !agreed ||
    password.length === 0 ||
    reasonValue.length < 3 ||
    intentTooShort;

  async function handleSubmit() {
    if (disabled) return;
    await onConfirm({
      password,
      reason: reasonValue,
      intent: intent.trim(),
    });
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!isSubmitting) onOpenChange(next);
      }}
    >
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileSignature className="h-4 w-4 text-primary" />
            {title}
          </DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        <div
          role="alert"
          className="flex items-start gap-2 rounded-md border border-amber-500/40 bg-amber-500/10 p-3 text-xs"
        >
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-600" />
          <span>
            This action is auditable and cannot be undone. Your signature will be
            bound to your account, timestamped, and stored in the immutable
            audit trail (21 CFR Part 11).
          </span>
        </div>

        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="esig-reason">Reason</Label>
            {hasPresets && (
              <select
                id="esig-reason"
                value={reasonSelect}
                onChange={(e) => setReasonSelect(e.target.value)}
                disabled={isSubmitting}
                className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
              >
                {reasonPresets!.map((p) => (
                  <option key={p.value} value={p.value}>
                    {p.label}
                  </option>
                ))}
                <option value="__custom__">Other — specify below</option>
              </select>
            )}
            {useCustomReason && (
              <Input
                id={hasPresets ? undefined : 'esig-reason'}
                placeholder="Specify a reason (min 3 chars)"
                value={reasonCustom}
                onChange={(e) => setReasonCustom(e.target.value)}
                disabled={isSubmitting}
              />
            )}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="esig-intent">
              Intent <span className="text-muted-foreground">(required, min {MIN_INTENT_LENGTH} chars)</span>
            </Label>
            <Textarea
              id="esig-intent"
              rows={3}
              placeholder="Explain what this signature means — e.g. 'Approving RMP v3.1 for authority submission after review of all Parts.'"
              value={intent}
              onChange={(e) => setIntent(e.target.value)}
              disabled={isSubmitting}
            />
            {intent.length > 0 && intentTooShort && (
              <p className="text-xs text-destructive">
                Intent must be at least {MIN_INTENT_LENGTH} characters.
              </p>
            )}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="esig-password">Password</Label>
            <Input
              id="esig-password"
              ref={passwordRef}
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={isSubmitting}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !disabled) {
                  e.preventDefault();
                  handleSubmit();
                }
              }}
            />
            <p className="text-[11px] text-muted-foreground">
              Re-authenticates your identity at the moment of signing, per 21 CFR 11.200.
            </p>
          </div>

          <label className="flex items-start gap-2 rounded-md border p-3 text-xs">
            <input
              type="checkbox"
              checked={agreed}
              onChange={(e) => setAgreed(e.target.checked)}
              disabled={isSubmitting}
              className="mt-0.5 h-3.5 w-3.5 shrink-0"
            />
            <span>
              I confirm this signature represents a legal equivalent to a
              handwritten signature (21 CFR Part 11).
            </span>
          </label>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            className={cn(disabled && 'cursor-not-allowed')}
            onClick={handleSubmit}
            disabled={disabled}
          >
            {isSubmitting ? 'Signing...' : submitLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
